import { useState, useEffect } from 'react';
import { useColorModeValue } from '@chakra-ui/react';

/**
 * A hook that safely uses useColorModeValue without causing SSR hydration mismatches.
 * Returns the light mode value during SSR and initial render, then updates to the
 * correct value once the component has mounted on the client side.
 *
 * @param lightValue - The value to use in light mode
 * @param darkValue - The value to use in dark mode
 * @returns The appropriate value based on color mode, safe for SSR
 */
export function useSSRSafeColorModeValue<T>(lightValue: T, darkValue: T): T {
  const [isClient, setIsClient] = useState(false);
  const colorModeValue = useColorModeValue(lightValue, darkValue);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // During SSR and initial render, always return the light value to prevent hydration mismatch
  if (!isClient) {
    return lightValue;
  }

  // Once mounted on client, return the actual color mode value
  return colorModeValue;
}

export default useSSRSafeColorModeValue;
